import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Lock, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// The static "Festival of Trees 2026" link points at the pre-existing
// editorial page /events/festival-of-trees. It stays in the menu regardless
// of what's in the events collection, per product decision.
const STATIC_EVENT_LINKS = [
  { name: 'Festival of Trees 2026', href: '/events/festival-of-trees' },
  { name: 'Donate a Tree', href: '/festival-of-trees/donate-a-tree' },
];

// Slugs to exclude from the dynamic events list to prevent duplicates
// with STATIC_EVENT_LINKS above.
const STATIC_EVENT_SLUG_BLACKLIST = new Set([
  'festival-of-trees',
  'festival-of-trees-2026',
]);

const baseNavLinks = [
  { 
    name: 'For Artisans',
    children: [
      { name: 'Start Free Trial', href: 'https://www.madeartisanmarkets.com', external: true, highlight: true },
      { name: 'Sell at Events', href: '/vendors' },
      { name: 'Learn (AMS University)', href: '/university' },
      { name: 'AMS Kids — Young CEOs', href: '/university/ams-kids' },
    ]
  },
  { name: 'About', href: '/about' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Contact', href: '/contact' },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [publishedEvents, setPublishedEvents] = useState([]);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setPublishedEvents(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Build the "Events we Host" menu: static Festival of Trees 2026 +
  // published dynamic events. Cap at 5 total items, then a divider +
  // "Show all events" link.
  const MAX_MENU_EVENTS = 5;
  const dynamicEventChildren = publishedEvents
    .filter((ev) => !STATIC_EVENT_SLUG_BLACKLIST.has(ev.slug))
    .map((ev) => ({ name: ev.title, href: `/events/${ev.slug}` }));

  const combinedEventChildren = [
    ...STATIC_EVENT_LINKS,
    ...dynamicEventChildren,
  ];
  const visibleEventChildren = combinedEventChildren.slice(0, MAX_MENU_EVENTS);
  const eventsChildren = [
    ...visibleEventChildren,
    { name: 'Show all events', href: '/happenings', muted: true, isSeeAll: true },
  ];

  const navLinks = [
    { name: 'Events we Host', children: eventsChildren },
    ...baseNavLinks,
  ];

  const isActive = (href) => location.pathname === href;

  const renderLink = (child) => {
    if (child.external) {
      return (
        <a 
          href={child.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full flex items-center gap-2 ${child.highlight ? 'text-primary font-semibold' : ''}`}
          data-testid={`nav-${child.name.toLowerCase().replace(/\s/g, '-')}`}
        >
          {child.highlight && <Sparkles className="w-4 h-4" />}
          {child.name}
        </a>
      );
    }
    return (
      <Link 
        to={child.href} 
        className={`w-full ${isActive(child.href) ? 'text-primary font-semibold' : ''} ${child.muted ? 'text-muted-foreground text-xs' : ''}`}
        data-testid={`nav-${child.name.toLowerCase().replace(/\s/g, '-')}`}
      >
        {child.name}
      </Link>
    );
  };

  return (
    <nav className="sticky top-0 z-50 w-full bg-white shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_craft-collective-11/artifacts/cpg0ir3f_AMS%20Logo.jpg" 
              alt="Artisan Market & Studio" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-sm font-medium" data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}>
                      {link.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-52">
                    {link.children.map((child, idx) => (
                      <span key={child.href || child.name} className="contents">
                        {child.isSeeAll && idx > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuItem asChild>
                          {renderLink(child)}
                        </DropdownMenuItem>
                      </span>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium transition-colors hover:text-primary flex items-center gap-1 ${
                    isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button - Start Free Trial */}
          <div className="hidden lg:flex items-center gap-3">
            <Link to="/admin" className="text-muted-foreground hover:text-primary transition-colors" data-testid="nav-admin">
              <Lock className="w-4 h-4" />
            </Link>
            <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full px-6 bg-primary hover:bg-primary/90" data-testid="nav-trial-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Start Free Trial
              </Button>
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle className="text-left font-serif">Navigation</SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-4">
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className={`text-lg font-medium transition-colors hover:text-primary ${
                    isActive('/') ? 'text-primary' : 'text-foreground'
                  }`}
                >
                  Home
                </Link>
                {navLinks.map((link) => (
                  link.children ? (
                    <div key={link.name} className="space-y-2">
                      <span className="text-sm font-medium text-muted-foreground">{link.name}</span>
                      <div className="pl-4 space-y-2">
                        {link.children.map((child) => (
                          child.external ? (
                            <a
                              key={child.name}
                              href={child.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => setIsOpen(false)}
                              className={`block text-lg font-medium transition-colors hover:text-primary ${
                                child.highlight ? 'text-primary flex items-center gap-2' : 'text-foreground'
                              }`}
                            >
                              {child.highlight && <Sparkles className="w-4 h-4" />}
                              {child.name}
                            </a>
                          ) : (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setIsOpen(false)}
                              className={`block text-lg font-medium transition-colors hover:text-primary ${
                                isActive(child.href) ? 'text-primary' : 'text-foreground'
                              } ${child.isSeeAll ? 'mt-2 pt-3 border-t border-border text-sm text-muted-foreground' : ''}`}
                            >
                              {child.name}
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`text-lg font-medium transition-colors hover:text-primary ${
                        isActive(link.href) ? 'text-primary' : 'text-foreground'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                ))}
                <a 
                  href="https://www.madeartisanmarkets.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                >
                  <Button className="w-full rounded-full mt-4" data-testid="mobile-trial-btn">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Free Trial
                  </Button>
                </a>
                <Link
                  to="/admin"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mt-4 pt-4 border-t"
                  data-testid="mobile-admin-link"
                >
                  <Lock className="w-4 h-4" />
                  Admin Login
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
